import { derived, get, writable } from 'svelte/store';
import { parseResponse } from '$lib/client/responseHelpers';

import type {
    OfferProduct,
    PaymentCard,
    PaymentMethodsList,
    RoomBalance,
    UserSubscription,
    UserTimePackage,
} from '@api/types';
import {
    buyCustomProduct,
    buyProductWithBillingPage,
    buyProductWithCard,
    getBillingCards,
    getPaymentMethods,
    getSubscription,
} from '@api/user';
import { checkGift, getOfferProducts } from '@api/user/offer';
import { dialogStore } from '@components/ui/dialog/store';
import { toastsStore } from '@components/ui/toasts';
import { MINUTE_IN_SECONDS, USER_MINUTES_BOUNDARY } from '@shared/constants';
import { URLS } from '@shared/routing';
import { Billing, type Nullable, PaymentMethod, type PaymentSource } from '@shared/types';
import { OfferType, ProductType } from '@shared/types/invoice';
import { toMapArray } from '@shared/utils/transformArray';

import {
    ff011_checkIfCryptoEligibleForSome,
    ff011_trySelectProductEligibleForCryptoPayment,
    ff011_tryUnselectCryptoIfProductTooCheap,
} from './experiments';
import { getTotalPrice } from './functions';
import type { PaymentMethodWithAlias, Store, StoreState } from './types';

const initialValues: StoreState = {
    subscriptions: [],
    timePackages: [],
    selectedCard: undefined,
    isTrial: false,
    selectedSubscriptionId: null,
    selectedTimePackageId: null,
    currentSubscription: null,
    currentTimePackage: null,
    isGiftChecked: false,
    timeBalance: null,
    isLoaded: false,
    paymentMethod: null,
    paymentMethods: [],
    billingCards: new Map(),
    visibleSubscriptions: [],
    visiblePaymentMethods: [],
    visibleTimePackages: [],
};

function createStore(): Store {
    const store = writable(initialValues);
    const { subscribe, update, set } = store;

    const updateStore = (data: Partial<StoreState>) => {
        update((state) => {
            return {
                ...state,
                ...data,
            };
        });
    };

    const canBuyMinutes = (state: StoreState): boolean => {
        return Boolean(state.currentSubscription?.isActive || state.currentSubscription?.isRebillNeed);
    };

    const setCurrentSubscription = (data: UserSubscription | null) => {
        update((state: StoreState) => {
            return {
                ...state,
                currentSubscription: data,
                isLoaded: true,
                isGiftChecked: !state.isGiftChecked
                    ? Boolean(data?.isActive || data?.subscribeSecondsLeft || data?.extraSecondsLeft)
                    : state.isGiftChecked,
                timeBalance: {
                    subscription: (data?.isActive && data?.subscribeSecondsLeft) || 0,
                    package: data?.extraSecondsLeft || 0,
                    hasPurchases: Boolean(data?.package),
                    extra: 0,
                },
            };
        });
    };

    const setCurrentTimePackage = (data: UserTimePackage | null) => {
        update((state) => {
            return {
                ...state,
                currentTimePackage: data,
            };
        });
    };

    const setDefaultPaymentMethod = (): PaymentMethodWithAlias => {
        const state = get(store);
        if (
            !state.paymentMethod
            || !state.visiblePaymentMethods.find(m => m.paymentMethod === state.paymentMethod?.name)
        ) {
            if (state.visiblePaymentMethods.length > 0) {
                let paymentMethod: PaymentMethodWithAlias;
                if (state.visiblePaymentMethods[0].aliases && state.visiblePaymentMethods[0].aliases.length) {
                    paymentMethod = {
                        name: state.visiblePaymentMethods[0].paymentMethod as PaymentMethod,
                        alias: state.visiblePaymentMethods[0].aliases[0],
                        billing: state.visiblePaymentMethods[0].billings[0].billing as Billing,
                    };
                } else {
                    paymentMethod = {
                        name: state.visiblePaymentMethods[0].paymentMethod as PaymentMethod,
                        billing: state.visiblePaymentMethods[0].billings[0].billing as Billing,
                    };
                }
                setPaymentMethod(paymentMethod);
                return paymentMethod;
            }
        }
        return state.paymentMethod as PaymentMethodWithAlias;
    };

    const selectNecessaryTimePackage = (necessarySeconds: number) => {
        update((state) => {
            const totalSeconds = calcTotalSeconds(state.timeBalance) || 0;
            const selectedTimePackageId =
                state.visibleTimePackages?.find((timePackage, i) => {
                    return (
                        necessarySeconds < totalSeconds + timePackage.seconds ||
                        i === state.visibleTimePackages.length - 1
                    );
                })?.id ?? null;
            return { ...state, selectedTimePackageId };
        });
    };

    const setTimeBalance = (value: NonNullable<RoomBalance['timeBalance']>) => {
        update((state) => {
            return {
                ...state,
                ...(state.currentSubscription
                    ? {
                        currentSubscription: {
                            ...state.currentSubscription,
                            ...(state.currentSubscription.isActive
                                ? { subscribeSecondsLeft: value.subscription }
                                : {}),
                            extraSecondsLeft: value.package + value.extra,
                        },
                    }
                    : {}),
                timeBalance: value,
            };
        });
    };

    const updateVisibleProducts = (productType: ProductType, isTrial = false) => {
        const state = get(store);
        if (productType === ProductType.Subscription) {
            const visibleSubscriptions = state.subscriptions
                .filter(s => isTrial || !s.discountPeriod);
            const selectedSubscriptionId = state.selectedSubscriptionId && visibleSubscriptions.length ?
                (
                    visibleSubscriptions.find(s => s.id === state.selectedSubscriptionId)
                        ? state.selectedSubscriptionId
                        : visibleSubscriptions.length > 1 ? visibleSubscriptions[1].id : visibleSubscriptions[0].id
                ) : state.selectedSubscriptionId;
            updateStore({
                selectedSubscriptionId,
                visibleSubscriptions,
            });
        }
        const visibleTimePackages = state.timePackages;
        updateStore({
            selectedTimePackageId: state.selectedTimePackageId && visibleTimePackages.length ?
                (
                    visibleTimePackages.find(s => s.id === state.selectedTimePackageId)
                        ? state.selectedTimePackageId
                        : visibleTimePackages.length > 1 ? visibleTimePackages[1].id : visibleTimePackages[0].id
                ) : state.selectedTimePackageId,
            visibleTimePackages,
        });
    };

    const updateVisiblePaymentMethods = (productType: ProductType): PaymentMethodWithAlias => {
        const state = get(store);
        const activeSubscription = state.subscriptions
            .find(s => s.id === state.selectedSubscriptionId);
        const activeTimePackage = state.timePackages
            .find(s => s.id === state.selectedTimePackageId);
        const activeProduct = productType === ProductType.Subscription
            ? activeSubscription : activeTimePackage;
        if (!activeProduct) {
            throw new Error('Active product not selected');
        }

        const products = productType === ProductType.Subscription
            ? state.visibleSubscriptions : state.visibleTimePackages;
        const paymentMethods = state.paymentMethods
            .map((pm) => {
                const productFullPrice = getTotalPrice(activeProduct.price, activeProduct.period);
                const availableBillings = pm.billings.filter(b => {
                    const paymentMethod = pm.paymentMethod as PaymentMethod;
                    if (ff011_checkIfCryptoEligibleForSome(state.isTrial, paymentMethod, products, pm.billings)) {
                        return true;
                    }

                    if (b.productPriceMin) {
                        if (productFullPrice < b.productPriceMin) {
                            return false;
                        }
                        if (!activeProduct.pricePerMinute) {
                            return false;
                        }
                    }
                    if (!activeProduct.paymentSettings) {
                        return true;
                    }
                    const ems = activeProduct.paymentSettings;
                    return !ems.some((exPm) => {
                        return exPm.excludedPaymentMethods?.includes(pm.paymentMethod) && exPm.billing === b.billing;
                    });
                });
                return { ...pm, billings: availableBillings };
            }).filter(p => p.billings.length > 0);
        updateStore({
            visiblePaymentMethods: paymentMethods,
        });
        return setDefaultPaymentMethod();
    };

    const setPaymentMethod = (newPaymentMethod: PaymentMethodWithAlias) => {
        const statePatch: Partial<StoreState> = { paymentMethod: newPaymentMethod };
        const {
            isTrial,
            selectedSubscriptionId,
            selectedTimePackageId,
            visibleSubscriptions,
            visibleTimePackages,
            visiblePaymentMethods,
        } = get(store);

        const productId = selectedSubscriptionId ? selectedSubscriptionId : selectedTimePackageId;
        const visibleProducts = selectedSubscriptionId ? visibleSubscriptions : visibleTimePackages;

        ff011_trySelectProductEligibleForCryptoPayment(
            isTrial,
            newPaymentMethod,
            productId,
            visibleProducts,
            visiblePaymentMethods,
            () => updateStore(statePatch),
            (eligibleProductId) => updateStore({
                ...statePatch,
                ...(selectedSubscriptionId
                    ? { selectedSubscriptionId: eligibleProductId }
                    : { selectedTimePackageId: eligibleProductId }),
            })
        );
    };

    return {
        set,
        update,
        subscribe,
        setDefaultBillingCard: (billing: Billing) => {
            update((state) => {
                const selected = state.billingCards.get(billing);
                return {
                    ...state,
                    selectedCard: selected && selected.length
                        ? selected[0]
                        : null,
                };
            });
        },
        loadBillingCards: async () => {
            const billingCards = toMapArray(await getBillingCards(), (b) => b.billing);
            updateStore({
                billingCards,
            });
        },
        setSelectedSubscriptionId: (value?: string, isTrial = false) => {
            let newSubscriptionId: OfferProduct['id'] | null = null;
            if (!value) {
                updateVisibleProducts(ProductType.Subscription);
                const data = get(store).visibleSubscriptions;
                const withDiscount = data.find((i) => i.discountPrice && !i.discountPeriod)?.id;
                newSubscriptionId = withDiscount || (data.length > 1 ? data[1].id : data[0].id);
                updateStore({
                    isTrial,
                    selectedSubscriptionId: newSubscriptionId,
                });
            } else {
                newSubscriptionId = value;
                updateStore({
                    isTrial,
                    selectedSubscriptionId: newSubscriptionId,
                });
                updateVisibleProducts(ProductType.Subscription, isTrial);
            }

            const { visibleSubscriptions, paymentMethod, visiblePaymentMethods } = get(store);
            ff011_tryUnselectCryptoIfProductTooCheap(
                isTrial,
                paymentMethod,
                newSubscriptionId,
                visibleSubscriptions,
                visiblePaymentMethods,
                setPaymentMethod);

            return updateVisiblePaymentMethods(ProductType.Subscription);
        },
        setSelectedCard: (value: PaymentCard | null) => {
            updateStore({
                selectedCard: value,
            });
        },
        setPaymentMethod,
        canBuyMinutes,
        setSelectedTimePackageId: (value?: string, priceSeconds?: number) => {
            let newTimePackageId: OfferProduct['id'] | null = null;
            if (!value) {
                updateVisibleProducts(ProductType.TimePackage);
                const timePacks = get(store).visibleTimePackages;
                newTimePackageId = timePacks ? (timePacks.length > 1 ? timePacks[1].id : timePacks[0].id) : null,
                updateStore({
                    selectedTimePackageId: newTimePackageId,
                });
                if (priceSeconds) {
                    selectNecessaryTimePackage(priceSeconds);
                }
            } else {
                newTimePackageId = value;
                updateStore({
                    selectedTimePackageId: newTimePackageId,
                });
                updateVisibleProducts(ProductType.TimePackage);
            }

            const { isTrial, visibleTimePackages, paymentMethod, visiblePaymentMethods } = get(store);
            ff011_tryUnselectCryptoIfProductTooCheap(
                isTrial,
                paymentMethod,
                newTimePackageId,
                visibleTimePackages,
                visiblePaymentMethods,
                setPaymentMethod);

            return updateVisiblePaymentMethods(ProductType.TimePackage);
        },
        buyProduct: async (
            productType: ProductType,
            paymentSource: PaymentSource,
            isAutoRenew: boolean
        ): Promise<string> => {
            const paymentStoreState = get(paymentStore);
            const selectedVariant = productType === ProductType.Subscription
                ? get(selectedSubscription)
                : get(selectedTimePackage);
            if (selectedVariant) {
                if (paymentStoreState.selectedCard) {
                    const { response, data } = await buyProductWithCard(
                        selectedVariant.id,
                        paymentStoreState.selectedCard.id,
                        paymentSource,
                        isAutoRenew
                    );
                    if (response.status === 201 && data?.result) {
                        return '';
                    }
                    if (data?.invoiceId) {
                        return URLS.paymentFailed(undefined, { invoice_id: data.invoiceId });
                    }
                    return URLS.paymentFailure(undefined, { paymentSource });
                } else if (paymentStoreState.paymentMethod?.name === PaymentMethod.GiftCard) {
                    const { response, data, error } = await buyCustomProduct(
                        productType,
                        paymentSource
                    );
                    if (error) {
                        toastsStore.error((error as Error).message);
                    }
                    if (response.status === 201 && data?.url && data?.result) {
                        return data.url;
                    }
                } else if (paymentStoreState.paymentMethod) {
                    const { response, data, error } = await buyProductWithBillingPage(
                        selectedVariant.id,
                        paymentStoreState.paymentMethod.name,
                        paymentSource,
                        paymentStoreState.paymentMethod.billing,
                        isAutoRenew
                    );
                    if (error) {
                        toastsStore.error((error as Error).message);
                    }
                    if (response.status === 201 && data?.url && data?.result) {
                        return data.url;
                    }
                    if (data?.invoiceId && paymentStoreState.paymentMethod.name === PaymentMethod.CreditCard) {
                        return URLS.paymentFailed(undefined, { invoice_id: data.invoiceId });
                    }
                }
            }
            return URLS.paymentFailure(undefined, { paymentSource, noBillingError: 'true' });
        },
        setCurrentSubscription,
        setCurrentTimePackage,
        showBuyDialog: (source: PaymentSource, giftPriceSeconds?: number) => {
            if (canBuyMinutes(get(store))) {
                dialogStore.showBuyMinutes({ source, giftPriceSeconds });
            } else {
                dialogStore.showBuyMembership({ source });
            }
        },
        loadPaymentMethods: async () => {
            if (get(store).paymentMethods.length) {
                return;
            }
            const response = await getPaymentMethods();
            const data = parseResponse(response) as PaymentMethodsList;
            updateStore({
                paymentMethods: data.paymentMethods,
            });
        },
        loadOfferProducts: async (offerType: OfferType) => {
            if (offerType === OfferType.Subscription || offerType === OfferType.TrialSubscription) {
                if (get(store).subscriptions.length) {
                    return;
                }
                const { response, data, error } = await getOfferProducts(offerType);
                if (response.status === 200) {
                    updateStore({
                        subscriptions: data?.products || [],
                    });
                } else {
                    toastsStore.error(error || 'Loading subscriptions error');
                }
            }
            if (offerType === OfferType.TimePackage) {
                const state = get(store);
                if (state.timePackages.length) {
                    return;
                }
                const { response, data, error } = await getOfferProducts(OfferType.TimePackage);
                if (response.status === 200) {
                    updateStore({
                        timePackages: data?.products || [],
                    });
                } else {
                    toastsStore.error(error || 'Loading packages error');
                }
            }
        },
        setFakeSubscription: () => {
            setCurrentSubscription({
                package: 'fake',
                endedAt: '',
                canceledAt: '',
                payment: '$0.01',
                subscribeSecondsLeft: 0,
                extraSecondsLeft: 0,
                hasGift: false,
                isRebillNeed: false,
                isAutoRenewEnabled: false,
                billing: null,
                isActive: true,
                canCancel: false,
            });
        },
        loadGift: async (): Promise<boolean> => {
            if (get(store).isGiftChecked) {
                return false;
            }
            const { data } = await checkGift();
            updateStore({ isGiftChecked: true });
            return data?.result ?? false;
        },
        loadSubscription: async (force?: boolean) => {
            if (get(store).isLoaded && !force) {
                return;
            }
            const { response, data } = await getSubscription();
            if (response.ok) {
                setCurrentSubscription(data || null);
            }
        },
        hasPermissionToSwitchCamAndMic: () => {
            const { currentSubscription } = get(store);
            const hasActiveSubscription = Boolean(currentSubscription?.isActive);
            const hasAnySubscription = Boolean(currentSubscription?.package)
                && (!currentSubscription?.canceledAt || hasActiveSubscription);
            const isAutoRenewDisabled = currentSubscription?.isAutoRenewEnabled === false;
            return hasAnySubscription && (hasActiveSubscription || !isAutoRenewDisabled);
        },
        setTimeBalance,
    };
}

export const paymentStore = createStore();

export const totalSeconds = derived(paymentStore, ($paymentStore) => {
    return calcTotalSeconds($paymentStore.timeBalance);
});

export const totalMinutes = derived(totalSeconds, ($totalSeconds) => {
    if ($totalSeconds) {
        return Math.round($totalSeconds / MINUTE_IN_SECONDS);
    }
    return null;
});

export const isMinutesRunningOut = derived(totalSeconds, ($totalSeconds) => {
    return $totalSeconds && $totalSeconds <= USER_MINUTES_BOUNDARY * MINUTE_IN_SECONDS;
});

export const selectedSubscription = derived(paymentStore, ($store) =>
    $store.selectedSubscriptionId
        ? $store.subscriptions.find(
            (subscription) => subscription.id === $store.selectedSubscriptionId
        ) || null
        : null
);

/** @deprecated */
export const selectedBilling = derived(
    paymentStore,
    ($store) =>
        $store.paymentMethod?.billing as Billing
);

export const visibleTimePackages = derived(
    [paymentStore],
    ([$paymentStore]) => {
        /** @todo удалить костыль когда эпоха сможет в 399 */
        if ($paymentStore.paymentMethod?.billing === Billing.Epoch) {
            return $paymentStore.visibleTimePackages.filter(p => p.price < 399_99);
        }
        return $paymentStore.visibleTimePackages;
    }
);

export const selectedTimePackage = derived([paymentStore, visibleTimePackages], ([$store, $visibleTimePackages]) =>
    $store.selectedTimePackageId
        ? $store.timePackages.find(
            (timePackage) => timePackage.id === $store.selectedTimePackageId
        ) || null
        : null
);

export const isCurrentSubscriptionActive = derived(
    paymentStore,
    ($store) => Boolean($store.currentSubscription?.isActive)
);

const calcTotalSeconds = (timeBalance: StoreState['timeBalance']): Nullable<number> => {
    if (!timeBalance) {
        return null;
    }
    const { subscription = 0, extra = 0 } = timeBalance;
    return subscription + (timeBalance.package || 0) + extra;
};
